import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

export type SignalCategory = 'project' | 'communication' | 'governance';
export type SignalSeverity = 'low' | 'medium' | 'high' | 'critical';

export type SignalReviewStatus = 'pending' | 'accepted' | 'rejected' | 'corrected';

export interface EnterpriseSignal {
  id: string;
  signal_category: SignalCategory;
  signal_type: string;
  severity: SignalSeverity;
  title: string;
  description: string | null;
  source: string;
  project_name: string | null;
  stakeholder: string | null;
  metadata: Record<string, unknown>;
  is_resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  review_status: SignalReviewStatus;
  review_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SignalStats {
  total: number;
  byCategory: {
    project: number;
    communication: number;
    governance: number;
  };
  bySeverity: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  unresolved: number;
}

export function useEnterpriseSignals() {
  const queryClient = useQueryClient();

  // Fetch all signals
  const signalsQuery = useQuery({
    queryKey: ['enterprise-signals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('enterprise_signals')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as EnterpriseSignal[];
    },
  });

  // Calculate stats from signals (never null to keep widgets safe)
  const stats: SignalStats = signalsQuery.data ? {
    total: signalsQuery.data.length,
    byCategory: {
      project: signalsQuery.data.filter(s => s.signal_category === 'project').length,
      communication: signalsQuery.data.filter(s => s.signal_category === 'communication').length,
      governance: signalsQuery.data.filter(s => s.signal_category === 'governance').length,
    },
    bySeverity: {
      low: signalsQuery.data.filter(s => s.severity === 'low').length,
      medium: signalsQuery.data.filter(s => s.severity === 'medium').length,
      high: signalsQuery.data.filter(s => s.severity === 'high').length,
      critical: signalsQuery.data.filter(s => s.severity === 'critical').length,
    },
    unresolved: signalsQuery.data.filter(s => !s.is_resolved).length,
  } : {
    total: 0,
    byCategory: { project: 0, communication: 0, governance: 0 },
    bySeverity: { low: 0, medium: 0, high: 0, critical: 0 },
    unresolved: 0,
  };

  // Generate signals mutation
  const generateMutation = useMutation({
    mutationFn: async ({ count = 5, category }: { count?: number; category?: SignalCategory }) => {
      const { data, error } = await supabase.functions.invoke('generate-signals', {
        body: { action: 'generate', count, category },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-signals'] });
    },
  });

  // Scan real synced data (Jira/Azure DevOps/ServiceNow/Microsoft Graph)
  // for rule-based signals — distinct from the synthetic generator above.
  const scanMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('generate-signals', {
        body: { action: 'scan' },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-signals'] });
    },
  });

  // Clear signals mutation
  const clearMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('generate-signals', {
        body: { action: 'clear' },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-signals'] });
    },
  });

  // HITL review mutation — the only way a signal moves out of "pending".
  // "accept" acts on the recommendation as-is, "reject" dismisses it as not
  // actionable, "correct" records a human edit before treating it as acted
  // on. Every path requires an explicit reviewer identity so a
  // recommendation can never silently become an action.
  const reviewMutation = useMutation({
    mutationFn: async ({
      id,
      status,
      reviewed_by,
      notes,
      correctedTitle,
      correctedDescription,
    }: {
      id: string;
      status: Exclude<SignalReviewStatus, 'pending'>;
      reviewed_by: string;
      notes?: string;
      correctedTitle?: string;
      correctedDescription?: string;
    }) => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('enterprise_signals')
        .update({
          review_status: status,
          review_notes: notes || null,
          reviewed_by,
          reviewed_at: now,
          ...(correctedTitle ? { title: correctedTitle } : {}),
          ...(correctedDescription !== undefined ? { description: correctedDescription } : {}),
          // Any explicit terminal review decision takes the signal off the
          // active queue, matching the legacy is_resolved-driven UI/filters.
          is_resolved: true,
          resolved_at: now,
          resolved_by: reviewed_by,
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-signals'] });
    },
  });

  // Set up realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('enterprise-signals-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'enterprise_signals',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['enterprise-signals'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    signals: signalsQuery.data || [],
    stats,
    isLoading: signalsQuery.isLoading,
    error: signalsQuery.error,
    generate: generateMutation.mutate,
    isGenerating: generateMutation.isPending,
    scan: scanMutation.mutate,
    isScanning: scanMutation.isPending,
    clear: clearMutation.mutate,
    isClearing: clearMutation.isPending,
    review: reviewMutation.mutate,
    isReviewing: reviewMutation.isPending,
    refetch: signalsQuery.refetch,
  };
}
